import { createContext, useContext } from 'react';

import type { TaskChoice } from '@/features/tasks';
import { actionFor, type HeldAction, type HeldActions } from './heldActions';
import type { QueueRow } from './yourTurn';

/** What Your turn's controls use to hold an action (spec 154 US2). */
export interface HoldRequest {
  choice: TaskChoice;
  /** The tasks: one, or a batch's siblings. */
  rows: readonly QueueRow[];
  /** The words on the row: "Done", "Done all 3". */
  label: string;
  /** Who it is for, in the announcement: a task's title, or "3 subtasks". */
  subject: string;
}

interface HeldActionsApi {
  state: HeldActions;
  hold: (request: HoldRequest) => boolean;
  undo: (id: string) => void;
}

export const HeldActionsContext = createContext<HeldActionsApi | null>(null);

export function useHeldActions(): HeldActionsApi {
  const api = useContext(HeldActionsContext);
  if (!api) throw new Error('useHeldActions outside HeldActionsProvider');
  return api;
}

/** One task's live action and refusal, for its row. */
export function useHeldFor(taskId: string): {
  action: HeldAction | undefined;
  refusal: string | undefined;
} {
  const { state } = useHeldActions();
  return {
    action: actionFor(state, taskId),
    refusal: state.refusals[taskId],
  };
}
